import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";
import type { DefinitionStatus } from "../forms/api";
import type { ChangeSet } from "../releases/api";

export type ConnectorKind = "Rest" | "Soap" | "Smtp" | "Sms" | "InboundWebhook" | "Mcp" | "Database";
export type ConnectorAuthKind = "None" | "ApiKey" | "Basic" | "Bearer" | "OAuth2ClientCredentials" | "MutualTls" | "Hmac";

export interface ConnectorParameterSpec { name: string; label?: string | null; labelAr?: string | null; description?: string | null; required?: boolean; default?: string | null; example?: string | null }
export interface ConnectorColumnSpec { name: string; label?: string | null; path?: string | null }

/** One call a connector offers, as stored in the definition (`operations[]`). */
export interface ConnectorOperationSpec {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  method: string;
  path: string;
  query?: Record<string, string>;
  headers?: Record<string, string>;
  requestTemplate?: string | null;
  parameters?: ConnectorParameterSpec[];
  responseMapping?: Record<string, string>;
  rowsPath?: string | null;
  columns?: ConnectorColumnSpec[];
  timeoutMs?: number | null;
  idempotent?: boolean;
  readOnly?: boolean;
}

export interface ConnectorEnvironmentOverride { baseUrl?: string; headers?: Record<string, string> }
export interface ConnectorUsage { kind: "agent" | "assistant" | "automation" | string; key: string; name: string; operations: string[] }

export interface ConnectorDefinition {
  id: string;
  key: string;
  version: number;
  status: DefinitionStatus;
  kind: ConnectorKind;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  tags: string[];
  baseUrl: string;
  owner?: string | null;
  authKind: ConnectorAuthKind;
  authConfig?: Record<string, unknown> | null;
  defaultHeaders?: Record<string, string> | null;
  operations?: ConnectorOperationSpec[] | null;
  timeoutMs: number;
  retryCount: number;
  retryBackoffMs: number;
  breakerFailureThreshold: number;
  breakerOpenSeconds: number;
  healthOperationKey?: string | null;
  environmentOverrides?: Record<string, ConnectorEnvironmentOverride> | null;
  logCalls: boolean;
  inboundPath?: string | null;
  inboundSecretRef?: string | null;
  enabled: boolean;
  changeNote?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  publishedByName?: string | null;
  publishedByChangeSetId?: string | null;
  sourcePackageKey?: string | null;
  usage: ConnectorUsage[];
}

/** The editable part of a connector, as sent to `PUT /api/platform/connectors/{id}`. */
export interface ConnectorBody {
  name: string;
  nameAr: string | null;
  description: string | null;
  descriptionAr: string | null;
  tags: string[];
  kind: ConnectorKind;
  baseUrl: string;
  owner: string | null;
  authKind: ConnectorAuthKind;
  authConfig: Record<string, unknown>;
  defaultHeaders: Record<string, string>;
  operations: ConnectorOperationSpec[];
  timeoutMs: number;
  retryCount: number;
  retryBackoffMs: number;
  breakerFailureThreshold: number;
  breakerOpenSeconds: number;
  healthOperationKey: string | null;
  environmentOverrides: Record<string, ConnectorEnvironmentOverride>;
  logCalls: boolean;
  inboundPath: string | null;
  inboundSecretRef: string | null;
  enabled: boolean;
}

export function connectorBodyOf(d: ConnectorDefinition): ConnectorBody {
  return {
    name: d.name, nameAr: d.nameAr ?? null, description: d.description ?? null, descriptionAr: d.descriptionAr ?? null, tags: d.tags ?? [], kind: d.kind,
    baseUrl: d.baseUrl, owner: d.owner ?? null, authKind: d.authKind, authConfig: d.authConfig ?? {}, defaultHeaders: d.defaultHeaders ?? {}, operations: d.operations ?? [],
    timeoutMs: d.timeoutMs, retryCount: d.retryCount, retryBackoffMs: d.retryBackoffMs, breakerFailureThreshold: d.breakerFailureThreshold, breakerOpenSeconds: d.breakerOpenSeconds,
    healthOperationKey: d.healthOperationKey ?? null, environmentOverrides: d.environmentOverrides ?? {}, logCalls: d.logCalls, inboundPath: d.inboundPath ?? null,
    inboundSecretRef: d.inboundSecretRef ?? null, enabled: d.enabled,
  };
}

export const EMPTY_CONNECTOR: ConnectorBody = {
  name: "", nameAr: null, description: null, descriptionAr: null, tags: [], kind: "Rest", baseUrl: "https://", owner: null, authKind: "None", authConfig: {}, defaultHeaders: {},
  operations: [], timeoutMs: 15_000, retryCount: 2, retryBackoffMs: 400, breakerFailureThreshold: 5, breakerOpenSeconds: 60, healthOperationKey: null, environmentOverrides: {},
  logCalls: true, inboundPath: null, inboundSecretRef: null, enabled: true,
};

export const EMPTY_OPERATION: ConnectorOperationSpec = {
  key: "", name: "", method: "GET", path: "/", query: {}, headers: {}, parameters: [], responseMapping: {}, rowsPath: null, columns: [], readOnly: true, idempotent: true,
};

export interface ConnectorSummary {
  key: string; name: string; nameAr?: string | null; description?: string | null; tags: string[];
  publishedVersion?: number | null; publishedId?: string | null; publishedAt?: string | null;
  draftVersion?: number | null; draftId?: string | null; draftStatus?: DefinitionStatus | null;
  latestVersion: number; versionCount: number; updatedAt: string; updatedByName?: string | null; sourcePackageKey?: string | null;
}

export interface ConnectorRow {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  kind: ConnectorKind;
  authKind: ConnectorAuthKind;
  baseUrl: string;
  owner?: string | null;
  tags: string[];
  definition: ConnectorSummary;
  enabled: boolean;
  operations: number;
  health: string;
  circuitOpen: boolean;
  calls24h: number;
  errors24h: number;
  p95?: number | null;
  lastCallAt?: string | null;
  usage: ConnectorUsage[];
}

export interface ConnectorAuthOption { kind: ConnectorAuthKind; fields: string[]; secrets: string[] }
export interface ConnectorSecretOption { key: string; reference: string; description?: string | null; hasValue: boolean }
export interface ConnectorOptions {
  kinds: ConnectorKind[];
  authKinds: ConnectorAuthOption[];
  secrets: ConnectorSecretOption[];
  environments: string[];
  platformEnvironment: string;
  simulatorsBaseUrl: string;
  builtins: Array<{ name: string; example?: string | null }>;
  methods: string[];
}

export interface ConnectorCatalogOperation { key: string; toolKey: string; name: string; nameAr?: string | null; description?: string | null; method: string; readOnly: boolean; parameters: ConnectorParameterSpec[]; columns: ConnectorColumnSpec[] }
export interface ConnectorCatalogEntry { key: string; name: string; nameAr?: string | null; description?: string | null; kind: ConnectorKind; operations: ConnectorCatalogOperation[] }

/** What `POST /{id}/test` returns: the runtime's call result, request and mapped response. */
export interface ConnectorCallResult {
  connectorKey: string;
  connectorName: string;
  operationKey: string;
  operationName: string;
  label?: string;
  version: number;
  environment: string;
  succeeded: boolean;
  statusCode: number;
  latencyMs: number;
  attempts: number;
  method: string;
  url: string;
  requestHeaders: Record<string, string>;
  requestBody?: string | null;
  fields: Record<string, unknown>;
  columns: Array<{ name: string; label?: string | null; path: string }>;
  rows: Array<Record<string, unknown>>;
  rawResponse?: string | null;
  error?: string | null;
  at: string;
}

export interface ConnectorCallRow {
  id: string; operation?: string | null; method: string; path: string; statusCode: number; latencyMs: number; succeeded: boolean;
  caseId?: string | null; caseNumber?: string | null; requestSummary?: string | null; responseSummary?: string | null; error?: string | null; createdAt: string;
}

const CN = ["platform", "connectors"] as const;

export function useConnectors(q?: string) {
  const api = useApi();
  return useQuery({ queryKey: [...CN, "list", q ?? ""], queryFn: () => api.get<ConnectorRow[]>(`/api/platform/connectors${q ? `?q=${encodeURIComponent(q)}` : ""}`), staleTime: 10_000 });
}

export function useConnector(key: string | undefined, version?: number) {
  const api = useApi();
  return useQuery({
    queryKey: [...CN, "one", key, version ?? "latest"],
    queryFn: () => api.get<ConnectorDefinition>(`/api/platform/connectors/${encodeURIComponent(key!)}${version ? `?version=${version}` : ""}`),
    enabled: !!key,
    staleTime: 5_000,
    retry: false,
  });
}

export function useConnectorOptions() {
  const api = useApi();
  return useQuery({ queryKey: [...CN, "options"], queryFn: () => api.get<ConnectorOptions>("/api/platform/connectors/options"), staleTime: 60_000 });
}

/** Published, enabled connectors and their operations: what agents, assistants and automations can be granted. */
export function useConnectorCatalog() {
  const api = useApi();
  return useQuery({ queryKey: [...CN, "catalog"], queryFn: () => api.get<ConnectorCatalogEntry[]>("/api/platform/connectors/catalog"), staleTime: 60_000 });
}

export function useConnectorCalls(key: string | undefined, poll = false) {
  const api = useApi();
  return useQuery({
    queryKey: [...CN, "calls", key],
    queryFn: () => api.get<ConnectorCallRow[]>(`/api/platform/connectors/${encodeURIComponent(key!)}/calls?take=100`),
    enabled: !!key,
    refetchInterval: poll ? 5_000 : false,
  });
}

export function useConnectorActions() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: [...CN] });
    void qc.invalidateQueries({ queryKey: ["platform", "change-sets"] });
    void qc.invalidateQueries({ queryKey: ["platform", "definitions"] });
    void qc.invalidateQueries({ queryKey: ["integrations"] });
  };
  return {
    create: useSave({ mutationFn: (input: { key: string; body: ConnectorBody; changeNote?: string }) => api.post<ConnectorDefinition>("/api/platform/connectors", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { id: string; body: ConnectorBody; changeNote?: string }) => api.put<ConnectorDefinition>(`/api/platform/connectors/${input.id}`, { body: input.body, changeNote: input.changeNote }), onSuccess: invalidate }),
    newVersion: useSave({ mutationFn: (input: { key: string; changeNote?: string }) => api.post<ConnectorDefinition>(`/api/platform/connectors/${encodeURIComponent(input.key)}/versions`, { changeNote: input.changeNote }), onSuccess: invalidate }),
    discard: useSave({ mutationFn: (id: string) => api.delete<void>(`/api/platform/connectors/${id}`), onSuccess: invalidate }),
    stage: useSave({ mutationFn: (input: { id: string; titleHint?: string }) => api.post<ChangeSet>(`/api/platform/connectors/${input.id}/stage`, { titleHint: input.titleHint }), onSuccess: invalidate }),
    withdraw: useSave({ mutationFn: (id: string) => api.post<ConnectorDefinition>(`/api/platform/connectors/${id}/withdraw`), onSuccess: invalidate }),
    test: useSave({
      // A test call the outside system failed (`succeeded: false`) is the test's result, not a refusal of the request: the answer is handed on
      // wrapped, so the save helper does not read it as one. A test stores no definition, so its failure is not a "Not saved".
      mutationFn: async (input: { id: string; operation: string; input: Record<string, string>; environment?: string | null }) =>
        ({ call: await api.post<ConnectorCallResult>(`/api/platform/connectors/${input.id}/test`, { operation: input.operation, input: input.input, environment: input.environment || null }) }),
      failed: t("admin:common.error"),
      onSuccess: () => { void qc.invalidateQueries({ queryKey: [...CN, "calls"] }); void qc.invalidateQueries({ queryKey: [...CN, "list"] }); },
    }),
  };
}

/** The runtime's defaults when an operation does not say: GET is a read and may be retried; anything else changes data. */
export const isReadOnly = (op: Pick<ConnectorOperationSpec, "readOnly" | "method">) => op.readOnly ?? (op.method ?? "GET").toUpperCase() === "GET";
export const isIdempotent = (op: Pick<ConnectorOperationSpec, "idempotent" | "method">) => op.idempotent ?? (op.method ?? "GET").toUpperCase() === "GET";

/** `{{name}}` placeholders used in a template string (built-ins such as `{{today}}` included). */
export function placeholdersIn(text: string | null | undefined): string[] {
  if (!text) return [];
  return [...new Set([...text.matchAll(/\{\{\s*([\w.-]+)\s*\}\}/g)].map((m) => m[1]!))];
}

/** Placeholders an operation uses (path, query, headers, body, rows path) that are neither parameters nor built-ins. */
export function undeclaredPlaceholders(op: ConnectorOperationSpec, builtins: string[]): string[] {
  const used = [
    ...placeholdersIn(op.path), ...Object.values(op.query ?? {}).flatMap(placeholdersIn), ...Object.values(op.headers ?? {}).flatMap(placeholdersIn),
    ...placeholdersIn(op.requestTemplate), ...placeholdersIn(op.rowsPath),
  ];
  const declared = new Set([...(op.parameters ?? []).map((p) => p.name.toLowerCase()), ...builtins.map((b) => b.toLowerCase()), "simulators"]);
  return [...new Set(used)].filter((u) => !declared.has(u.toLowerCase()));
}
